/**
 * The customer surface is a single live source over `app.customers_v1`.
 *
 * `source` is retained as a one-value prop threaded from the route layer to
 * hooks (API base) and link helpers (SPA path prefix) — a vestige of the
 * former live/sandbox split, now collapsed to live-only after the demo
 * sandbox was removed. Kept as a constant so the threading stays uniform.
 *
 * See context/plans/2026-06-13-remove-demo-live-customer-edits.md.
 *
 * Named for the schema the surface reads. It was `'exigo_mirror'`, which named
 * the wrong schema — nothing here reads the mirror.
 */
export type CustomerSource = 'app';

export const DEFAULT_SOURCE: CustomerSource = 'app';

const CUSTOMERS_API_BASE: Record<CustomerSource, string> = { app: '/api/customers' };
const ORDERS_API_BASE:    Record<CustomerSource, string> = { app: '/api/orders' };
const CUSTOMERS_LIST_PATH: Record<CustomerSource, string> = { app: '/customers' };

/** API base prefix used by the customer hooks. */
export function customersApiBase(source: CustomerSource): string {
  return CUSTOMERS_API_BASE[source];
}

/** API base prefix for the orders detail/timeline/lookup hooks. */
export function ordersApiBase(source: CustomerSource): string {
  return ORDERS_API_BASE[source];
}

/** SPA route prefix for the customers list. */
export function customersListPath(source: CustomerSource): string {
  return CUSTOMERS_LIST_PATH[source];
}

/** SPA route for a single customer's detail page. */
export function customerDetailPath(source: CustomerSource, customerId: string | number): string {
  return `${customersListPath(source)}/${customerId}`;
}

/** SPA route for an order detail page nested under a customer. */
export function orderDetailPath(
  source: CustomerSource,
  customerId: string | number,
  orderId: string | number,
): string {
  return `${customerDetailPath(source, customerId)}/orders/${orderId}`;
}

/** SPA route for the orders tab nested under a customer. */
export function customerOrdersPath(source: CustomerSource, customerId: string | number): string {
  return `${customerDetailPath(source, customerId)}/orders`;
}
